import * as twgl from 'twgl.js';
import frag from './glsl/main.frag';
import vert from './glsl/main.vert';

import rectFrag from './glsl/rect.frag';
import rectVert from './glsl/rect.vert';

import noiseTex from '../../../img/noise512.png';
import gradTex from '../../../img/ksa-grad.png';

// config vars
const WHITE_NOISE_SIZE = Math.pow(1024, 2);
const MOUSE_LERP = 0.01;

function getWhiteNoise() {
  const a = new Array(WHITE_NOISE_SIZE);
  for (let i = 0; i < WHITE_NOISE_SIZE; i++) {
    a[i] = Math.floor(Math.random() * 256);
  }
  return a;
}

class ShaderBG {
  constructor() {
    // set initial time:
    this.count = 0;
    this.glRender = this.glRender.bind(this);
    this.uniforms = {
      uSpeed: 0.05,
      u2: 0,
      uScale: 0.9,
    };
    this.ready = false;

    this.el = document.querySelector('.spectrum__canvas');

    this.initializeGL();
  }

  initializeGL() {
    const canvas = this.el;

    this.gl = canvas.getContext('webgl');

    // compile main shader
    this.programInfo = twgl.createProgramInfo(this.gl, [vert, frag]);

    // compile shader for fbo texture
    this.rectProgramInfo = twgl.createProgramInfo(this.gl, [rectVert, rectFrag]);

    // hard-set canvas to full screen
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // build the rect we're drawing on
    const boxSize = 1;

    const arrays = {
      position: [
        -boxSize,
        -boxSize,
        0,
        boxSize,
        -boxSize,
        0,
        -boxSize,
        boxSize,
        0,
        -boxSize,
        boxSize,
        0,
        boxSize,
        -boxSize,
        0,
        boxSize,
        boxSize,
        0,
      ],
    };
    this.bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);

    // setup textures
    this.textures = twgl.createTextures(
      this.gl,
      {
        noise: {
          src: noiseTex,
          mag: this.gl.LINEAR,
          min: this.gl.LINEAR,
        },
        gradient: {
          src: gradTex,
          mag: this.gl.LINEAR,
          min: this.gl.LINEAR,
        },
        whitenoise: {
          mag: this.gl.NEAREST,
          min: this.gl.LINEAR,
          src: getWhiteNoise(),
        },
      },
      () => this.glRender(1),
    );

    // setup mouse var and listeners
    this.mousePos = [0.8, 0.5];
    this.mouseVel = [0, 0];
    this.newPos = [0.8, 0.5];
    window.addEventListener('mousemove', this.handleMouseMove.bind(this));

    this.ready = true;
  }

  handleMouseMove(e) {
    this.newPos = [e.clientX / window.innerWidth, 1 - e.clientY / window.innerHeight];
  }

  // gl render loop
  glRender(time) {
    // wait until we're ready
    if (this.ready === false) {
      requestAnimationFrame(this.glRender);
      return;
    }

    // lerp the mouse variables
    [0, 1].map(i => {
      this.mouseVel[i] = (this.newPos[i] - this.mousePos[i]) * -1;
      this.mousePos[i] += (this.newPos[i] - this.mousePos[i]) * MOUSE_LERP;
    });

    const width = this.el.width;
    const height = this.el.height;

    this.gl.viewport(0, 0, width, height);

    const aspect =
      window.innerWidth < window.innerHeight
        ? [window.innerWidth / window.innerHeight, 1]
        : [1, window.innerHeight / window.innerWidth];

    let scroll =
      (document.documentElement.scrollTop || document.body.scrollTop) / document.body.offsetHeight;
    scroll *= document.body.offsetHeight / window.innerHeight;

    const uniforms = {
      time: time % 500000,
      scroll: scroll,
      resolution: [width, height],
      aspect: aspect,
      noise: this.textures.noise,
      whitenoise: this.textures.whitenoise,
      gradient: this.textures.gradient,
      mousePos: this.mousePos,
      mouseVel: this.mouseVel,
      uSpeed: this.uniforms.uSpeed,
      u2: this.uniforms.u2,
      uScale: this.uniforms.uScale,
    };

    // target the current fbo
    twgl.bindFramebufferInfo(this.gl);
    this.gl.useProgram(this.programInfo.program);

    // render the scene out to the target buffer
    twgl.setBuffersAndAttributes(this.gl, this.programInfo, this.bufferInfo);
    twgl.setUniforms(this.programInfo, uniforms);
    twgl.drawBufferInfo(this.gl, this.bufferInfo);

    requestAnimationFrame(this.glRender);
  }
}

export default ShaderBG;
